import "./QuestionBoxAccountMention.css";
import {
	createAtomLeadingVisual,
	createQuestionBoxAtom,
	setAtomLeadingImage,
} from "./QuestionBoxAtom";
import type { DocMentionData } from "./questionDocument";

/**
 * Builds an account-mention (`@handle`) chip for insertion into the QuestionBox
 * contenteditable: the blue variant of the shared atom (see QuestionBoxAtom),
 * led by the account's avatar, or by an `account_circle` glyph when the mention
 * arrived without one (as a pasted chip does).
 */
export function createQuestionBoxAccountMention(
	data: DocMentionData,
	options: { onRemove: (element: HTMLElement) => void },
): HTMLElement {
	return createQuestionBoxAtom({
		variant: "account",
		label: `@${data.handle}`,
		leading: createAtomLeadingVisual(data.avatarUrl),
		data: {
			mentionType: data.type,
			mentionHandle: data.handle,
			mentionPlatform: data.platform || undefined,
			mentionId: data.id || undefined,
		},
		onRemove: options.onRemove,
	});
}

/** True when `node` is an account-mention chip produced by this module. */
export function isAccountMentionElement(node: Node): node is HTMLElement {
	return (
		node instanceof HTMLElement && node.dataset.mentionHandle !== undefined
	);
}

/**
 * Shows the resolved avatar on every chip in `root` whose account has one, for
 * avatars that arrive after the chips do (see useQuestionBox's
 * resolveMentionAvatars). Chips whose account isn't in the map keep their glyph.
 */
export function showAccountMentionAvatars(
	root: HTMLElement,
	avatarUrlById: Map<string, string>,
): void {
	for (const [id, avatarUrl] of avatarUrlById) {
		const chips = root.querySelectorAll<HTMLElement>(
			`[data-mention-id="${CSS.escape(id)}"]`,
		);
		for (const chip of chips) setAtomLeadingImage(chip, avatarUrl);
	}
}
